import React, { useState, useEffect } from 'react';
import { useLeagueSettings } from '../../context/LeagueSettingsContext';
import { getByeWeekRequirement, updateByeWeekRequirement, triggerNoPicks, getAvailableWeeks } from '../../adminApi';
import { useCurrentWeek } from '../../context/CurrentWeekContext';
import styles from '../../styles/AdminDashboard.module.css';

const LeagueManagementPanel = () => {
  const {
    settings,
    loading,
    error,
    updateLeagueSettings,
    getSettingsDescription
  } = useLeagueSettings();

  const { currentWeek } = useCurrentWeek();

  const [localSettings, setLocalSettings] = useState({
    show_locks: true,
    show_lockers: true,
    allow_duplicates: false,
    allow_future_week_locks: false,
    apply_next_week: false,
    week_transition_point: 'conclusion_of_last_lockable_game'
  });

  const [byeWeekRequired, setByeWeekRequired] = useState(null);
  const [byeWeekLoading, setByeWeekLoading] = useState(true);
  const [byeWeekSaving, setByeWeekSaving] = useState(false);
  const [byeWeekError, setByeWeekError] = useState(null);

  const [isUpdating, setIsUpdating] = useState(false);
  const [updateMessage, setUpdateMessage] = useState('');

  // No-pick trigger state
  const [availableWeeks, setAvailableWeeks] = useState([]);
  const [selectedWeek, setSelectedWeek] = useState('');
  const [noPickLoading, setNoPickLoading] = useState(false);
  const [noPickMessage, setNoPickMessage] = useState('');

  // Initialize local settings when context settings load
  useEffect(() => {
    if (!loading && settings) {
      setLocalSettings({
        show_locks: settings.show_locks,
        show_lockers: settings.show_lockers,
        allow_duplicates: settings.allow_duplicates,
        allow_future_week_locks: settings.allow_future_week_locks,
        apply_next_week: settings.apply_next_week,
        week_transition_point: settings.week_transition_point || 'conclusion_of_last_lockable_game'
      });
    }
  }, [settings, loading]);

  // Load bye week requirement setting
  useEffect(() => {
    loadByeWeekRequirement();
  }, []);

  // Load available weeks for no-pick trigger
  useEffect(() => {
    loadAvailableWeeks();
  }, []);

  // Debug: Monitor byeWeekRequired state changes
  useEffect(() => {
    console.log('byeWeekRequired state changed to:', byeWeekRequired);
  }, [byeWeekRequired]);

  const loadByeWeekRequirement = async () => {
    try {
      setByeWeekLoading(true);
      setByeWeekError(null);
      const response = await getByeWeekRequirement();
      console.log('Bye week API response:', response);
      console.log('Response data:', response.data);
      console.log('Setting byeWeekRequired to:', response.data.bye_week_required);
      setByeWeekRequired(response.data.bye_week_required);
    } catch (err) {
      console.error('Error loading bye week requirement:', err);
      setByeWeekError('Failed to load bye week requirement setting');
    } finally {
      setByeWeekLoading(false);
    }
  };

  const loadAvailableWeeks = async () => {
    try {
      const response = await getAvailableWeeks();
      setAvailableWeeks(response.data);
    } catch (err) {
      console.error('Error loading available weeks:', err);
    }
  };

  const handleNoPickTrigger = async () => {
    if (!selectedWeek) {
      setNoPickMessage('Please select a week');
      return;
    }

    try {
      setNoPickLoading(true);
      setNoPickMessage('');
      const response = await triggerNoPicks(parseInt(selectedWeek));
      setNoPickMessage(`✅ ${response.data.message}`);
    } catch (err) {
      console.error('Error triggering no-pick processing:', err);
      setNoPickMessage(`❌ Failed to trigger no-pick processing: ${err.response?.data?.error || err.message}`);
    } finally {
      setNoPickLoading(false);
    }
  };

  const handleByeWeekToggle = async () => {
    try {
      setByeWeekSaving(true);
      setByeWeekError(null);

      const newValue = !byeWeekRequired;
      await updateByeWeekRequirement(newValue);
      setByeWeekRequired(newValue);
    } catch (err) {
      console.error('Error updating bye week requirement:', err);
      setByeWeekError('Failed to update bye week requirement setting');
    } finally {
      setByeWeekSaving(false);
    }
  };

  // Process toggle dependencies when show_locks changes
  useEffect(() => {
    if (localSettings.show_locks === false) {
      setLocalSettings(prev => ({
        ...prev,
        allow_duplicates: true
      }));
    } else if (localSettings.show_locks === true) {
      setLocalSettings(prev => ({
        ...prev,
        allow_duplicates: false
      }));
    }
  }, [localSettings.show_locks]);

  const handleToggleChange = (settingKey, value) => {
    setLocalSettings(prev => ({
      ...prev,
      [settingKey]: value
    }));
  };

  const handleSave = async () => {
    setIsUpdating(true);
    setUpdateMessage('');

    try {
      const result = await updateLeagueSettings(localSettings);
      if (result.success) {
        setUpdateMessage(`✅ ${result.message}`);
      } else {
        setUpdateMessage(`❌ ${result.message}`);
      }
    } catch (error) {
      setUpdateMessage(`❌ Failed to update settings: ${error.message}`);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCancel = () => {
    setLocalSettings({
      show_locks: settings.show_locks,
      show_lockers: settings.show_lockers,
      allow_duplicates: settings.allow_duplicates,
      allow_future_week_locks: settings.allow_future_week_locks,
      apply_next_week: settings.apply_next_week,
      week_transition_point: settings.week_transition_point || 'conclusion_of_last_lockable_game'
    });
    setUpdateMessage('');
  };

  const hasChanges = () => {
    return (
      localSettings.show_locks !== settings.show_locks ||
      localSettings.show_lockers !== settings.show_lockers ||
      localSettings.allow_duplicates !== settings.allow_duplicates ||
      localSettings.allow_future_week_locks !== settings.allow_future_week_locks ||
      localSettings.apply_next_week !== settings.apply_next_week ||
      localSettings.week_transition_point !== (settings.week_transition_point || 'conclusion_of_last_lockable_game')
    );
  };

  if (loading) {
    return (
      <div className={styles.adminPanel}>
        <h3>League Management</h3>
        <div className={styles.loading}>Loading league settings...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.adminPanel}>
        <h3>League Management</h3>
        <div className={styles.error}>Error: {error}</div>
      </div>
    );
  }

  return (
    <div className={styles.adminPanel}>
      <h3>League Management</h3>

      <div className={styles.settingsSection}>
        <h4>Visibility Controls</h4>

        <div className={styles.settingRow}>
          <label className={styles.settingLabel}>
            <input
              type="checkbox"
              checked={localSettings.show_locks}
              onChange={(e) => handleToggleChange('show_locks', e.target.checked)}
              className={styles.settingCheckbox}
            />
            Show Locks
          </label>
          <span className={styles.settingDescription}>
            Users can see what picks have been locked
          </span>
        </div>

        <div className={styles.settingRow}>
          <label className={styles.settingLabel}>
            <input
              type="checkbox"
              checked={localSettings.show_lockers}
              onChange={(e) => handleToggleChange('show_lockers', e.target.checked)}
              className={styles.settingCheckbox}
            />
            Show Lockers
          </label>
          <span className={styles.settingDescription}>
            Users can see who made the locks (after they make their own lock)
          </span>
        </div>
      </div>

      <div className={styles.settingsSection}>
        <h4>Week Management</h4>

        <div className={styles.settingRow}>
          <label className={styles.settingLabel}>
            Week Transition Point
          </label>
          <span className={styles.settingDescription}>
            Control when weeks advance from one week to the next
          </span>
        </div>
        <div className={styles.settingRow}>
          <select
            value={localSettings.week_transition_point}
            onChange={(e) => handleToggleChange('week_transition_point', e.target.value)}
            className={styles.weekSelect}
          >
            <option value="conclusion_of_last_lockable_game">Conclusion of last lockable game</option>
            <option value="cfdb_default">CFDB Default</option>
            <option value="tuesday_1201_am">12:01 AM Tuesday</option>
            <option value="start_of_last_locked_game">Start of last locked game</option>
            <option value="completion_of_all_user_locks">Completion of all user locks</option>
          </select>
        </div>
      </div>

      <div className={styles.settingsSection}>
        <h4>Pick Rules</h4>

        <div className={styles.settingRow}>
          <label className={`${styles.settingLabel} ${!localSettings.show_locks ? styles.disabled : ''}`}>
            <input
              type="checkbox"
              checked={localSettings.allow_duplicates}
              onChange={(e) => handleToggleChange('allow_duplicates', e.target.checked)}
              disabled={!localSettings.show_locks}
              className={styles.settingCheckbox}
            />
            Allow Duplicate Locks
          </label>
          <span className={styles.settingDescription}>
            Multiple users can lock the same pick
            {!localSettings.show_locks && (
              <span className={styles.disabledNote}>
                (Required when locks are hidden to ensure fair play)
              </span>
            )}
          </span>
        </div>

        <div className={styles.settingRow}>
          <label className={styles.settingLabel}>
            <input
              type="checkbox"
              checked={localSettings.allow_future_week_locks}
              onChange={(e) => handleToggleChange('allow_future_week_locks', e.target.checked)}
              className={styles.settingCheckbox}
            />
            Allow Future Week Locks
          </label>
          <span className={styles.settingDescription}>
            Users can lock picks in future weeks (with risk warning)
          </span>
        </div>
      </div>

      <div className={styles.settingsSection}>
        <h4>Timing Controls</h4>

        <div className={styles.settingRow}>
          <label className={styles.settingLabel}>
            <input
              type="checkbox"
              checked={localSettings.apply_next_week}
              onChange={(e) => handleToggleChange('apply_next_week', e.target.checked)}
              className={styles.settingCheckbox}
            />
            Apply Changes at Start of Next Week
          </label>
          <span className={styles.settingDescription}>
            Changes take effect when the next week starts (prevents unfair situations)
          </span>
        </div>
      </div>

      <div className={styles.settingsSection}>
        <h4>Bye Week Requirements</h4>

        {byeWeekError && (
          <div className={styles.error}>
            {byeWeekError}
          </div>
        )}

        <div className={styles.settingRow}>
          <label className={styles.settingLabel}>
            <input
              type="checkbox"
              checked={byeWeekRequired === true}
              onChange={handleByeWeekToggle}
              disabled={byeWeekLoading || byeWeekSaving}
              className={styles.settingCheckbox}
            />
            Bye Week Required
          </label>
          <span className={styles.settingDescription}>
            {byeWeekRequired === true ? (
              <>
                <strong>Required Mode (Current Behavior):</strong>
                <ul>
                  <li>Users must use their bye week before making their final pick of the season</li>
                  <li>If they reach the last lock without using bye, system forces bye usage</li>
                  <li>If they forget to make a lock and haven't used bye, bye is auto-applied when last game kicks off</li>
                  <li>If bye already used, results in no_pick and automatic loss</li>
                </ul>
              </>
            ) : byeWeekRequired === false ? (
              <>
                <strong>Optional Mode (New Behavior):</strong>
                <ul>
                  <li>Users can choose to use bye week at any time during the season</li>
                  <li>No requirement to use bye before final pick</li>
                  <li>If they forget to make a lock and haven't used bye, bye is auto-applied</li>
                  <li>If they reach end of season with unused bye, it's applied to their largest margin loss</li>
                  <li>This effectively erases their worst statistical performance</li>
                </ul>
              </>
            ) : (
              <span>Loading...</span>
            )}
            {byeWeekSaving && <span className={styles.saving}>Saving...</span>}
          </span>
        </div>
      </div>

      <div className={styles.settingsSection}>
        <h4>No-Pick Processing</h4>
        <p className={styles.settingDescription}>
          Manually trigger no-pick processing for users who didn't make enough picks in a specific week.
          This will apply bye weeks (if available) or create no-pick losses for users who should have been processed automatically.
        </p>

        <div className={styles.settingRow}>
          <label className={styles.settingLabel}>
            Select Week:
            <select
              value={selectedWeek}
              onChange={(e) => setSelectedWeek(e.target.value)}
              className={styles.weekSelect}
              disabled={noPickLoading}
            >
              <option value="">Choose a week...</option>
              {availableWeeks
                .filter(week => week < currentWeek) // Only show past weeks
                .map(week => (
                  <option key={week} value={week}>
                    Week {week}
                  </option>
                ))}
            </select>
          </label>
        </div>

        {noPickMessage && (
          <div className={`${styles.updateMessage} ${noPickMessage.includes('✅') ? styles.success : styles.error}`}>
            {noPickMessage}
          </div>
        )}

        <div className={styles.actionButtons}>
          <button
            onClick={handleNoPickTrigger}
            disabled={noPickLoading || !selectedWeek}
            className={styles.saveButton}
          >
            {noPickLoading ? 'Processing...' : 'Trigger No-Pick Processing'}
          </button>
        </div>
      </div>

      <div className={styles.currentConfiguration}>
        <h5>Current Configuration</h5>
        <p>
          {getSettingsDescription()}
        </p>
      </div>

      {updateMessage && (
        <div className={`${styles.updateMessage} ${updateMessage.includes('✅') ? styles.success : styles.error}`}>
          {updateMessage}
        </div>
      )}

      <div className={styles.actionButtons}>
        <button
          onClick={handleSave}
          disabled={isUpdating || !hasChanges()}
          className={styles.saveButton}
        >
          {isUpdating ? 'Saving...' : 'Save Changes'}
        </button>

        {hasChanges() && (
          <button
            onClick={handleCancel}
            disabled={isUpdating}
            className={styles.cancelButton}
          >
            Cancel
          </button>
        )}
      </div>
    </div>
  );
};

export default LeagueManagementPanel;

